/**
 * DELIVERY PERFORMANCE REPORT PAGE
 *
 * Analyze delivery success rates, timing, and route efficiency.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Spinner,
  Select,
  DataTable,
  Badge,
  ProgressBar,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface DailyPerformance {
  delivery_date: string;
  total_routes: number;
  completed_routes: number;
  total_stops: number;
  avg_distance_km: number;
  avg_duration_minutes: number;
}

interface PerformanceReport {
  daily_performance: DailyPerformance[];
  stop_breakdown: Record<string, number>;
  summary: {
    total_deliveries: number;
    completed: number;
    failed: number;
    success_rate: number | string;
  };
  filters: {
    start_date: string;
    end_date: string;
  };
}

export default function DeliveryPerformanceReport() {
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [report, setReport] = useState<PerformanceReport | null>(null);
  const [period, setPeriod] = useState('month');

  const loadReport = useCallback(async () => {
    const endDate = new Date().toISOString().split('T')[0];
    let startDate: string;

    switch (period) {
      case 'week':
        startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case '3months':
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'year':
        startDate = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      default:
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }

    const data = await fetchApi<PerformanceReport>(`/reports/delivery-performance?start_date=${startDate}&end_date=${endDate}`);
    if (data) {
      setReport(data);
    }
  }, [fetchApi, period]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    });
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDistance = (km: number | null) => {
    if (!km) return '-';
    return `${km.toFixed(1)} km`;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      completed: 'Completed',
      failed: 'Failed',
      pending: 'Pending',
      in_progress: 'In Progress',
      cancelled: 'Cancelled',
      skipped: 'Skipped',
    };
    return labels[status] || status;
  };

  const getStatusTone = (status: string): 'success' | 'critical' | 'attention' | 'info' | undefined => {
    const tones: Record<string, 'success' | 'critical' | 'attention' | 'info'> = {
      completed: 'success',
      failed: 'critical',
      pending: 'attention',
      in_progress: 'info',
    };
    return tones[status];
  };

  if (loading && !report) {
    return (
      <Page title="Delivery Performance">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading report...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const successRate = report?.summary?.success_rate
    ? (typeof report.summary.success_rate === 'string'
        ? parseFloat(report.summary.success_rate)
        : report.summary.success_rate)
    : 0;

  return (
    <Page
      title="Delivery Performance"
      subtitle="Analyze delivery success rates and efficiency"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Period Filter */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end">
              <div style={{ width: '200px' }}>
                <Select
                  label="Period"
                  options={[
                    { label: 'This Week', value: 'week' },
                    { label: 'This Month', value: 'month' },
                    { label: 'Last 3 Months', value: '3months' },
                    { label: 'This Year', value: 'year' },
                  ]}
                  value={period}
                  onChange={setPeriod}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {report && (
          <>
            {/* Summary Cards */}
            <Layout.Section>
              <InlineStack gap="400" wrap>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Total Deliveries</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold">
                      {report.summary.total_deliveries}
                    </Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Completed</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold" tone="success">
                      {report.summary.completed}
                    </Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Failed</Text>
                    <Text as="p" variant="headingXl" fontWeight="bold" tone="critical">
                      {report.summary.failed}
                    </Text>
                  </BlockStack>
                </Card>
                <Card>
                  <BlockStack gap="200">
                    <Text as="p" tone="subdued">Success Rate</Text>
                    <Text
                      as="p"
                      variant="headingXl"
                      fontWeight="bold"
                      tone={successRate >= 95 ? 'success' : successRate >= 85 ? undefined : 'critical'}
                    >
                      {successRate.toFixed(1)}%
                    </Text>
                  </BlockStack>
                </Card>
              </InlineStack>
            </Layout.Section>

            {/* Success Rate Progress */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Overall Success Rate</Text>
                  <BlockStack gap="200">
                    <ProgressBar
                      progress={successRate}
                      size="small"
                      tone={successRate >= 95 ? 'success' : 'primary'}
                    />
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Target: 95%</Text>
                      <Text as="span" fontWeight="semibold">{successRate.toFixed(1)}%</Text>
                    </InlineStack>
                  </BlockStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Stop Status Breakdown */}
            <Layout.Section variant="oneHalf">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Delivery Status Breakdown</Text>

                  {Object.keys(report.stop_breakdown).length > 0 ? (
                    <BlockStack gap="300">
                      {Object.entries(report.stop_breakdown).map(([status, count]) => (
                        <InlineStack key={status} align="space-between">
                          <Badge tone={getStatusTone(status)}>{getStatusLabel(status)}</Badge>
                          <Text as="span" fontWeight="semibold">{count}</Text>
                        </InlineStack>
                      ))}
                    </BlockStack>
                  ) : (
                    <Text as="p" tone="subdued">No delivery data for this period.</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Insights */}
            <Layout.Section variant="oneHalf">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Insights</Text>

                  {successRate >= 95 ? (
                    <Banner tone="success">
                      Excellent! Your delivery success rate of {successRate.toFixed(1)}% exceeds the 95% target.
                    </Banner>
                  ) : successRate >= 85 ? (
                    <Banner tone="warning">
                      Your success rate of {successRate.toFixed(1)}% is below the 95% target.
                      Review failed deliveries to identify patterns.
                    </Banner>
                  ) : (
                    <Banner tone="critical">
                      Your success rate of {successRate.toFixed(1)}% needs attention.
                      Consider reviewing delivery windows, customer communication, and route planning.
                    </Banner>
                  )}

                  {report.summary.failed > 0 && (
                    <Text as="p">
                      <strong>{report.summary.failed} deliveries failed</strong> during this period.
                      Common causes include: customer not home, wrong address, or access issues.
                    </Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Daily Performance */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Daily Performance</Text>

                  {report.daily_performance.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                      headings={['Date', 'Routes', 'Completed', 'Stops', 'Avg Distance', 'Avg Duration']}
                      rows={report.daily_performance.map((day) => [
                        formatDate(day.delivery_date),
                        day.total_routes,
                        day.completed_routes,
                        day.total_stops || 0,
                        formatDistance(day.avg_distance_km),
                        formatDuration(day.avg_duration_minutes),
                      ])}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No route data for this period.</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
